import React, { useEffect, useState } from 'react';

const FADE_MS = 7000;

/**
 * Bottom captions bar. Shows Sage transcript finals with a slow fade-out.
 */
export default function CaptionsOverlay({
  lines,
  visible,
  sagePresent,
  unavailableHint,
  isHost,
}) {
  const [fadedIds, setFadedIds] = useState(() => new Set());

  const finals = (lines || []).filter((l) => !l.partial).slice(-3);
  const lineKeys = finals.map((l) => l.id || `${l.speaker}-${l.ts}`).join('|');

  // Reset fade state when a new final line arrives.
  useEffect(() => {
    setFadedIds((prev) => {
      const active = new Set(finals.map((l) => l.id || `${l.speaker}-${l.ts}`));
      const next = new Set();
      prev.forEach((id) => {
        if (active.has(id)) next.add(id);
      });
      return next;
    });
  }, [lineKeys]); // eslint-disable-line react-hooks/exhaustive-deps

  if (unavailableHint) {
    return (
      <div className="gg-captions gg-captions--hint" role="status" aria-live="polite">
        <div className="gg-captions__empty">
          {isHost
            ? 'Invite Sage to enable live captions'
            : 'Captions are available when Sage is in the meeting'}
        </div>
      </div>
    );
  }

  if (!visible) return null;

  if (!sagePresent) {
    return (
      <div className="gg-captions gg-captions--hint" role="status" aria-live="polite">
        <div className="gg-captions__empty">
          Captions unavailable — Sage is not in this meeting
        </div>
      </div>
    );
  }

  const visibleLines = finals.filter(
    (line) => !fadedIds.has(line.id || `${line.speaker}-${line.ts}`)
  );
  const hasLines = visibleLines.length > 0;

  return (
    <div
      className={`gg-captions${hasLines ? '' : ' gg-captions--idle'}`}
      role="status"
      aria-live="polite"
    >
      {hasLines ? (
        visibleLines.map((line) => {
          const key = line.id || `${line.speaker}-${line.ts}`;
          return (
            <div
              key={key}
              className="gg-captions__line"
              style={{ animationDuration: `${FADE_MS}ms` }}
              onAnimationEnd={() => {
                setFadedIds((prev) => new Set(prev).add(key));
              }}
            >
              {line.speaker ? (
                <span className="gg-captions__speaker">{line.speaker}: </span>
              ) : null}
              <span className="gg-captions__text">{line.text}</span>
            </div>
          );
        })
      ) : (
        <div className="gg-captions__empty">Listening for speech…</div>
      )}
    </div>
  );
}
